import isSpacedString from '@/helpers/isSpacedString'
import axios from '@/lib/axios'
import { useSearchHotelsStore } from '@/stores/hotelsSearch'
import { useQuery } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { set } from 'lodash'
import { storeToRefs } from 'pinia'
import { MaybeRef, isRef, watch } from 'vue'

import { HotelSearch } from '../interfaces/hotelsSearch'

interface SearchParams {
	q: string
	params?: Props
}

interface Props {
	filters?: MaybeRef<{ [key: string]: string | number | boolean | undefined }>
}

const getSearchHotels = async (params: SearchParams) => {
	const data = await axios.get<HotelSearch[]>('/api/hotels', {
		params: {
			q: params.q,
			...(isRef(params.params?.filters) ?
				params.params?.filters.value
			:	params.params?.filters),
		},
	})

	return data
}

export const useSearchHotels = (props: Props = {}) => {
	const { filters } = props

	const {
		setResultsSearch,
		setSearchQueryHotels,
		setResultsFavorites,
		setResultsIndependents,
		resetSearchQueryHotels,
		restSearchResults,
		resetSearchResultsIndependents,
	} = useSearchHotelsStore()
	const {
		resultsHotelsSearch,
		resultsHotelsFavorites,
		resultsHotelsIndependents,
		searchQueryHotels,
	} = storeToRefs(useSearchHotelsStore())

	const { data, error, refetch, isFetching } = useQuery(
		['searchHotels'],
		() =>
			getSearchHotels({
				q: searchQueryHotels.value,
				params: { filters },
			}),
		{
			enabled: true,
		}
	)

	const searchHotels = useDebounceFn(async () => {
		if (searchQueryHotels.value && !isSpacedString(searchQueryHotels.value)) {
			refetch()
		}
	}, 100)

	watch(data, (value) => {
		if (value) {
			setResultsIndependents(
				value.data.filter((hotel) => hotel.isIndependent === true)
			)
			setResultsSearch(value.data.filter((hotel) => !hotel.isFavorite))
			setResultsFavorites(value.data.filter((hotel) => hotel.isFavorite))
		}
	})

	watch(searchQueryHotels, (value) => {
		value ? searchHotels() : restSearchResults()
	})

	return {
		hotels: data,
		searchHotels,
		resultsHotelsSearch,
		resultsHotelsFavorites,
		setSearchQueryHotels,
		searchQueryHotels,
		resetSearchQueryHotels,
		restSearchResults,
		resetSearchResultsIndependents,
		resultsHotelsIndependents,
		isFetching,
		error,
		refetch,
	}
}
